import emailjs from "@emailjs/browser";
import { emailConfig, isVisitorEmailConfigured } from "../config/email";

const STORAGE_KEY = "visitor_ping_last_sent_at";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const LOCATION_TIMEOUT_MS = 4500;

const fetchJsonWithTimeout = async (url) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), LOCATION_TIMEOUT_MS);

  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`Location lookup failed: ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timeoutId);
  }
};

const locationProviders = [
  {
    name: "ipapi.co",
    lookup: async () => {
      const data = await fetchJsonWithTimeout("https://ipapi.co/json/");
      return {
        ip: data.ip,
        city: data.city,
        region: data.region,
        country: data.country_name,
        postal: data.postal,
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timezone,
        org: data.org,
      };
    },
  },
  {
    name: "ipwho.is",
    lookup: async () => {
      const data = await fetchJsonWithTimeout("https://ipwho.is/");
      if (data.success === false) throw new Error(data.message || "ipwho.is lookup failed");

      return {
        ip: data.ip,
        city: data.city,
        region: data.region,
        country: data.country,
        postal: data.postal,
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timezone?.id,
        org: data.connection?.org || data.connection?.isp,
      };
    },
  },
  {
    name: "geolocation-db.com",
    lookup: async () => {
      const data = await fetchJsonWithTimeout("https://geolocation-db.com/json/");
      return {
        ip: data.IPv4,
        city: data.city,
        region: data.state,
        country: data.country_name,
        postal: data.postal,
        latitude: data.latitude,
        longitude: data.longitude,
      };
    },
  },
];

const getBrowserFallbackLocation = () => ({
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  language: navigator.language,
  location_source: "browser_fallback",
  location_status: "ip_lookup_failed",
});

const hasUsefulLocation = (loc) => !!(loc.ip || loc.city || loc.region || loc.country);

const getApproxLocation = async () => {
  for (const provider of locationProviders) {
    try {
      const loc = await provider.lookup();
      if (hasUsefulLocation(loc)) {
        return {
          ...loc,
          location_source: provider.name,
          location_status: "ip_lookup_success",
        };
      }
    } catch {
      // Try the next provider. Frontend IP lookups are often blocked by privacy tools.
    }
  }

  return getBrowserFallbackLocation();
};

export const sendVisitorPing = async ({ pageUrl, referrer }) => {
  if (!isVisitorEmailConfigured) return { ok: false, reason: "not_configured" };

  const last = Number(localStorage.getItem(STORAGE_KEY) || "0");
  const now = Date.now();
  if (last && now - last < ONE_DAY_MS) return { ok: false, reason: "throttled" };

  const loc = await getApproxLocation();

  const payload = {
    page_url: pageUrl,
    referrer: referrer || "direct",
    user_agent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    timestamp: new Date().toISOString(),
    ip: loc.ip || "unknown",
    city: loc.city || "unknown",
    region: loc.region || "unknown",
    country: loc.country || "unknown",
    postal: loc.postal || "unknown",
    latitude: loc.latitude || "unknown",
    longitude: loc.longitude || "unknown",
    timezone: loc.timezone || "unknown",
    org: loc.org || "unknown",
    location_source: loc.location_source || "unknown",
    location_status: loc.location_status || "unknown",
    maps_link:
      loc.latitude && loc.longitude
        ? `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`
        : "unknown",
  };

  try {
    await emailjs.send(
      emailConfig.serviceId,
      emailConfig.visitorTemplateId,
      payload,
      emailConfig.publicKey
    );
    localStorage.setItem(STORAGE_KEY, String(now));
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: "send_failed", error: err };
  }
};
